import { handlePostApi, handleUpdatePostApi } from '@/api/crud';
import { barcodeApiEndPoints, branchAccessEndpoints } from '@/api/endpoints';
import { VoucherFooterButton } from '@/pages/vouchers/VoucherComponent';
import { optionSearchFunction } from '@/utils/common';
import {
  Table,
  Checkbox,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Button,
  message,
  Flex,
} from 'antd';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { chunk } from 'lodash';
import { useEffect } from 'react';
import optionList from '@/utils/barcode/barcodePrintTags';
import GlobalDivider from '@/utils/divider/GlobalDivider';
import LoadingOverlay from '@/utils/loading/loadingOverlay';
import { Back } from '../back';

const AddPRN = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [branchList, setBranchList] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { branchId, formatId } = location.state || [];
  const [selectedLayout, setSelectedLayout] = useState(null);
  const addLayout = () => {
    if (!selectedLayout) {
      handlePostApi(
        barcodeApiEndPoints.createPrintLayout,
        form.getFieldsValue(),
        setLoading,
        form,
      );
    } else {
      handleUpdatePostApi(
        selectedLayout.referenceId,
        barcodeApiEndPoints.updatePrintLayout,
        form.getFieldsValue(),
        setLoading,
        form,
        () => {},
        navigate(-1),
      );
    }
  };

  const chunkedOptions = chunk(optionList, 4);

  const confirmTemplateCopy = (template) => {
    Modal.confirm({
      title: 'Confirm Copy',
      content: 'Do you want to copy the template to the clipboard?',
      onOk: async () => {
        try {
          if (template.type === 'text') {
            await navigator.clipboard.writeText(`<${template.value}>`);
            message.success('Template copied to clipboard!');
          } else if (template.type === 'info') {
            await navigator.clipboard.writeText(
              `<InfoValue:${template.value}>`,
            );
            message.success('Template copied to clipboard!');
          }
        } catch (err) {
          message.error('Failed to copy template.');
          console.error('Error copying template:', err);
        }
      },
      onCancel: () => {
        message.info('Copy canceled.');
      },
      okText: 'Yes, Copy',
      cancelText: 'No, Cancel',
    });
  };

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({ branchId: branchId });

    if (selectedLayout) {
      form.setFieldsValue(selectedLayout);
    }
  }, [selectedLayout, form]);

  useEffect(() => {
    setLoading(true);
    async function fetchBarcodeLayouts() {
      const branches = await branchAccessEndpoints.retrieveList();
      const printLayouts = await barcodeApiEndPoints.printLayouts();
      setSelectedLayout(
        printLayouts.data.data.find(
          (layout) => layout.referenceId === formatId,
        ),
      );
      setBranchList(branches.data.data);
      setLoading(false);
    }
    fetchBarcodeLayouts();
  }, []);

  return (
    <div>
      <LoadingOverlay loading={loading} />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Back />
        <GlobalDivider name="PRN Settings" />
      </div>
      <div style={{ padding: '20px' }}>
        <Form
          layout="vertical"
          className="username"
          style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}
          form={form}
          onFinish={addLayout}
        >
          <Form.Item
            rules={[{ required: true, message: 'Please input format name' }]}
            label="Format Name"
            name="heading"
            required
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Branch"
            rules={[{ required: true, message: 'Please input branch ID' }]}
            name="branchId"
            required
          >
            <Select
              placeholder="Select branch"
              filterOption={(value, option) => {
                optionSearchFunction(value, option);
              }}
              options={branchList?.map((item) => ({
                value: item.referenceId,
                label: item?.name,
              }))}
            />
          </Form.Item>

          <Row gutter={[8, 8]} justify="space-between">
            <Form.Item>
              <Checkbox.Group>
                <Table
                  dataSource={chunkedOptions.map((row, index) => ({
                    key: index,
                    col1: row[0] || null,
                    col2: row[1] || null,
                    col3: row[2] || null,
                    col4: row[3] || null,
                  }))}
                  pagination={false}
                  bordered
                >
                  <Table.Column
                    dataIndex="col1"
                    render={(item) =>
                      item && (
                        <>
                          <Button onClick={() => confirmTemplateCopy(item)}>
                            {item.display} ({item.label})
                          </Button>
                          <Flex
                            wrap
                            gap={8}
                            style={{ width: '60%', marginTop: '16px' }}
                          >
                            {item.keys ? 'Keys:' : ''}
                            {item.keys?.map((key) => (
                              <p key={key}>{key},</p>
                            ))}
                          </Flex>
                        </>
                      )
                    }
                  />
                  <Table.Column
                    dataIndex="col2"
                    render={(item) =>
                      item && (
                        <>
                          <Button onClick={() => confirmTemplateCopy(item)}>
                            {item.display} ({item.label})
                          </Button>
                          <Flex
                            wrap
                            gap={8}
                            style={{ width: '60%', marginTop: '16px' }}
                          >
                            {item.keys ? 'Keys:' : ''}
                            {item.keys?.map((key) => (
                              <p key={key}>{key},</p>
                            ))}
                          </Flex>
                        </>
                      )
                    }
                  />
                  <Table.Column
                    dataIndex="col3"
                    render={(item) =>
                      item && (
                        <>
                          <Button onClick={() => confirmTemplateCopy(item)}>
                            {item.display} ({item.label})
                          </Button>
                          <Flex
                            wrap
                            gap={8}
                            style={{ width: '60%', marginTop: '16px' }}
                          >
                            {item.keys ? 'Keys:' : ''}
                            {item.keys?.map((key) => (
                              <p key={key}>{key},</p>
                            ))}
                          </Flex>
                        </>
                      )
                    }
                  />
                  <Table.Column
                    dataIndex="col4"
                    render={(item) =>
                      item && (
                        <>
                          <Button onClick={() => confirmTemplateCopy(item)}>
                            {item.display} ({item.label})
                          </Button>
                          <Flex
                            wrap
                            gap={8}
                            style={{ width: '60%', marginTop: '16px' }}
                          >
                            {item.keys ? 'Keys:' : ''}
                            {item.keys?.map((key) => (
                              <p key={key}>{key},</p>
                            ))}
                          </Flex>
                        </>
                      )
                    }
                  />
                </Table>
              </Checkbox.Group>
            </Form.Item>
          </Row>

          <Form.Item label="PRN Code" name="prnCode" required>
            <Input.TextArea rows={15} />
          </Form.Item>

          <Flex gap={16}>
            <Button htmlType="submit" disabled={loading}>
              Save
            </Button>
            <Button
              type="primary"
              danger
              onClick={() => {
                navigate(-1);
              }}
            >
              Cancel
            </Button>
          </Flex>
        </Form>
      </div>
    </div>
  );
};

export default AddPRN;
